import { NestedLayout, PartOut, Sheet } from '@cutr/nesting';
import { useTranslation } from 'react-i18next';

import { useCutlistParts } from '@/api/store';
import Card from '@/blocks/Card';
import { InfoMessage } from '@/blocks/InfoMessage';
import { Pagination } from '@/blocks/Pagination';
import { Button } from '@/primitives/Button';
import { Checkmark, Icon, Loader } from '@/primitives/Icons';

import { SmallPartEdgebandingNestingNotices } from './Nesting';
import styles from './Nesting.module.css';
import { RenderSheet } from './NestingRenderer';

export type NestedGroup = {
  id: string;
  layouts: NestedLayout[];
};

const layoutSizeCounts = (layouts: NestedLayout[]): [string, number][] => {
  const counts = new Map<string, number>();
  layouts.forEach((layout) => {
    const key = `${layout.lengthMM} x ${layout.widthMM}`;
    counts.set(key, (counts.get(key) ?? 0) + layout.count);
  });
  return [...counts.entries()];
};

const toSheet = (
  parts: Omit<PartOut, 'sheetId'>[],
  sheetId: number
): Sheet => ({
  sheetId,
  parts: parts.map((part) => ({ ...part, sheetId })),
  usedAreaMM2: 0,
  totalAreaMM2: 0,
  sawingLengthMM: 0,
});

export const CalculatePriceButton = ({
  onCalculate,
  canCalculate,
  calculateLabel,
  small,
  isComputing,
}: {
  onCalculate?: () => void;
  canCalculate?: boolean;
  calculateLabel?: string;
  small?: boolean;
  isComputing?: boolean;
}) => {
  const { t } = useTranslation();

  if (isComputing) {
    return (
      <Button name="calculatePrice" disabled size={small ? 's' : undefined}>
        <span>{t('cutlist-summary.nesting.computing')}</span>
        <Icon icon={<Loader />} size={small ? 1.5 : 2.5} />
      </Button>
    );
  }
  if (!onCalculate) return null;

  return (
    <Button
      name="calculatePrice"
      onClick={onCalculate}
      disabled={!canCalculate}
      size={small ? 's' : undefined}
    >
      {calculateLabel ?? t('cutlist-summary.nesting.calculatePriceCta')}
    </Button>
  );
};

export const StarterTierNesting = ({
  groups,
  onCalculate,
  canCalculate,
  calculateLabel,
  smallCalculate,
  complete,
  isComputing,
  dirty,
  failureMessage,
}: {
  groups: NestedGroup[];
  onCalculate?: () => void;
  canCalculate?: boolean;
  calculateLabel?: string;
  smallCalculate?: boolean;
  complete?: boolean;
  isComputing?: boolean;
  dirty?: boolean;
  failureMessage?: string | null;
}) => {
  const { t } = useTranslation();
  const parts = useCutlistParts();

  const calculateButton = (
    <CalculatePriceButton
      onCalculate={onCalculate}
      canCalculate={canCalculate}
      calculateLabel={calculateLabel}
      small={smallCalculate}
      isComputing={isComputing}
    />
  );

  return (
    <Card>
      <div className="flexAlign opposites">
        <h3>{t('cutlist-summary.nesting.title')}</h3>
        {smallCalculate &&
          (complete ? (
            <span className="flexAlign gap-xs">
              {t('cutlist-summary.nesting.nestingComplete')}
              <Icon icon={<Checkmark />} size={1.5} />
            </span>
          ) : (
            calculateButton
          ))}
      </div>
      {failureMessage && (
        <InfoMessage message={failureMessage} variant="error" />
      )}
      <p />

      {!groups.length && (
        <p>
          {t(
            canCalculate
              ? 'cutlist-summary.nesting.calculatePlaceholder'
              : 'cutlist-summary.nesting.emptyPlaceholder'
          )}
        </p>
      )}

      <div>
        {groups.map((group) => {
          const maxW = Math.max(...group.layouts.map((l) => l.lengthMM));
          const maxH = Math.max(...group.layouts.map((l) => l.widthMM));

          return (
            <div key={group.id} id={group.id}>
              <div className="opposites">
                <div className={styles.info}>
                  <label>{t(`cutlist-summary.nesting.partInfo.size`)}</label>
                  {layoutSizeCounts(group.layouts).map(
                    ([dimensionString, count]) => (
                      <div
                        key={dimensionString}
                        className={styles.selectedDimension}
                      >
                        {`${dimensionString} (${t(
                          `cutlist-summary.nesting.sheetCount`,
                          { count }
                        )})`}
                      </div>
                    )
                  )}
                </div>
              </div>

              <Pagination navKey="sheet">
                {group.layouts.flatMap((layout, layoutIdx) =>
                  Array.from({ length: layout.count }, (_, copyIdx) => (
                    <div
                      key={`${layoutIdx}-${copyIdx}`}
                      style={{
                        aspectRatio: `${maxW} / ${maxH}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <RenderSheet
                        size={[layout.lengthMM, layout.widthMM]}
                        sheet={toSheet(layout.parts, layoutIdx)}
                        sheetEdgeTrimConfig={null}
                        wrapperWidth={`${(layout.lengthMM / maxW) * 100}%`}
                      />
                    </div>
                  ))
                )}
              </Pagination>
            </div>
          );
        })}
      </div>

      {groups.length > 0 && (
        <SmallPartEdgebandingNestingNotices parts={parts} />
      )}

      {dirty && !isComputing && (
        <InfoMessage
          message={t('cutlist-summary.nesting.reRunRequired')}
          variant="warning"
        />
      )}
    </Card>
  );
};
